import { getSessionUUID } from '../components/lib/AuthToken';
import logger from '../utils/logger';

const buildOptions = async (method = 'GET', body = null) => {
    const sessionToken = await getSessionUUID();
    const options = {
        method,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionToken}`,
        },
    };

    if (body !== null) {
        options.body = JSON.stringify(body);
    }

    return options;
};

export const fetchIncidents = async (filters = {}) => {
    try {
        const response = await fetch('/incidents/list', await buildOptions('POST', filters));
        const data = await response.json();
        return { status: response.status, ...data };
    } catch (err) {
        logger.error('Error fetching incidents:', err);
        return { status: 500, error: 'Failed to fetch incidents' };
    }
};

export const createIncident = async (payload) => {
    try {
        const response = await fetch('/incidents/', await buildOptions('POST', payload));
        const data = await response.json();
        return { status: response.status, ...data };
    } catch (err) {
        logger.error('Error creating incident:', err);
        return { status: 500, error: 'Failed to create incident' };
    }
};

export const fetchIncidentById = async (incidentId) => {
    try {
        const response = await fetch(`/incidents/${incidentId}`, await buildOptions('GET'));
        const data = await response.json();
        return { status: response.status, ...data };
    } catch (err) {
        logger.error('Error fetching incident details:', err);
        return { status: 500, error: 'Failed to fetch incident details' };
    }
};

export const updateIncident = async (incidentId, payload) => {
    try {
        const response = await fetch(`/incidents/${incidentId}`, await buildOptions('PUT', payload));
        const data = await response.json();
        return { status: response.status, ...data };
    } catch (err) {
        logger.error('Error updating incident:', err);
        return { status: 500, error: 'Failed to update incident' };
    }
};

export const addIncidentAction = async (incidentId, payload) => {
    try {
        const response = await fetch(`/incidents/${incidentId}/actions`, await buildOptions('POST', payload));
        const data = await response.json();
        return { status: response.status, ...data };
    } catch (err) {
        logger.error('Error adding incident action:', err);
        return { status: 500, error: 'Failed to add incident action' };
    }
};

export const uploadDashcam = (incidentId, file, onProgress) => {
    return new Promise(async (resolve) => {
        try {
            const sessionToken = await getSessionUUID();
            const formData = new FormData();
            formData.append('file', file);
            const xhr = new XMLHttpRequest();
            xhr.open('POST', `/incidents/${incidentId}/dashcam`);
            xhr.setRequestHeader('Authorization', `Bearer ${sessionToken}`);
            xhr.withCredentials = true;
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable && onProgress) {
                    onProgress(Math.round((event.loaded / event.total) * 100));
                }
            };
            xhr.onload = () => {
                try {
                    const data = JSON.parse(xhr.responseText);
                    resolve({ status: xhr.status, ...data });
                } catch {
                    resolve({ status: xhr.status, error: 'Failed to parse response' });
                }
            };
            xhr.onerror = () => resolve({ status: 500, error: 'Network error during upload' });
            xhr.send(formData);
        } catch (err) {
            logger.error('Error uploading dashcam:', err);
            resolve({ status: 500, error: 'Failed to upload dashcam footage' });
        }
    });
};

export const uploadTachograph = async (incidentId, file) => {
    try {
        const sessionToken = await getSessionUUID();
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`/incidents/${incidentId}/tachograph`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${sessionToken}` },
            body: formData,
        });
        const data = await response.json();
        return { status: response.status, ...data };
    } catch (err) {
        logger.error('Error uploading tachograph:', err);
        return { status: 500, error: 'Failed to upload tachograph print' };
    }
};

export const uploadIncidentImage = async (incidentId, file) => {
    try {
        const sessionToken = await getSessionUUID();
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`/incidents/${incidentId}/images`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${sessionToken}` },
            body: formData,
        });
        const data = await response.json();
        return { status: response.status, ...data };
    } catch (err) {
        logger.error('Error uploading incident image:', err);
        return { status: 500, error: 'Failed to upload incident image' };
    }
};

export const uploadSupportingDocument = async (incidentId, file) => {
    try {
        const sessionToken = await getSessionUUID();
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`/incidents/${incidentId}/supporting-documents`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${sessionToken}` },
            body: formData,
        });
        const data = await response.json();
        return { status: response.status, ...data };
    } catch (err) {
        logger.error('Error uploading supporting document:', err);
        return { status: 500, error: 'Failed to upload supporting document' };
    }
};

export const downloadIncidentReport = async (incidentId) => {
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch(`/incidents/${incidentId}/report`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${sessionToken}` },
        });
        if (!response.ok) {
            const data = await response.json();
            return { status: response.status, error: data.error || 'Failed to download report' };
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `incident-report-${incidentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return { status: 200 };
    } catch (err) {
        logger.error('Error downloading incident report:', err);
        return { status: 500, error: 'Failed to download report' };
    }
};
